import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, CircularProgress, Pagination,
  ToggleButtonGroup, ToggleButton, Avatar, Tooltip, TextField,
  InputAdornment, Divider, Button, Dialog, DialogContent,
  DialogTitle, IconButton, Tabs, Tab,
} from "@mui/material";
import SearchIcon                    from "@mui/icons-material/Search";
import FlightTakeoffIcon             from "@mui/icons-material/FlightTakeoff";
import BadgeOutlinedIcon             from "@mui/icons-material/BadgeOutlined";
import FamilyRestroomOutlinedIcon    from "@mui/icons-material/FamilyRestroomOutlined";
import SchoolOutlinedIcon            from "@mui/icons-material/SchoolOutlined";
import PaidOutlinedIcon              from "@mui/icons-material/PaidOutlined";
import EmailOutlinedIcon             from "@mui/icons-material/EmailOutlined";
import CheckCircleOutlineIcon        from "@mui/icons-material/CheckCircleOutline";
import CloseIcon                     from "@mui/icons-material/Close";
import PersonOutlineIcon             from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon             from "@mui/icons-material/PhoneOutlined";
import SummarizeOutlinedIcon         from "@mui/icons-material/SummarizeOutlined";
import LocationCityOutlinedIcon      from "@mui/icons-material/LocationCityOutlined";
import CalendarMonthOutlinedIcon     from "@mui/icons-material/CalendarMonthOutlined";
import axios from "axios";
import { BASE_URL } from "../../services/configUrls";


// ─────────────────────────────────────────────────────────────────────────────
// Shared helpers
// ─────────────────────────────────────────────────────────────────────────────

const YesNoChip = ({ value, yesColor = "success", noColor = "error" }) => {
  if (value === null || value === undefined || value === "")
    return <Typography sx={{ fontSize: "0.62rem", color: "#bbb" }}>—</Typography>;
  return (
    <Chip
      label={value}
      size="small"
      color={value === "Yes" ? yesColor : noColor}
      sx={{ fontSize: "0.6rem", fontWeight: 700, height: 18, "& .MuiChip-label": { px: 0.8 } }}
    />
  );
};

const AmountChip = ({ value }) => {
  if (!value) return <Typography sx={{ fontSize: "0.62rem", color: "#bbb" }}>—</Typography>;
  const num = Number(value);
  const fmt = isNaN(num) ? value : "₹" + num.toLocaleString("en-IN");
  return (
    <Chip label={fmt} size="small" variant="outlined"
      sx={{ fontSize: "0.6rem", fontWeight: 700, height: 18, borderColor: "#fb8c00", color: "#fb8c00", "& .MuiChip-label": { px: 0.8 } }}
    />
  );
};

const initials  = (name) => !name ? "?" : name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase();
const avatarBg  = (name) => {
  const cols = ["#1565c0","#6a1b9a","#00695c","#ad1457","#e65100","#37474f","#558b2f","#0277bd"];
  return !name ? cols[0] : cols[name.charCodeAt(0) % cols.length];
};
const fmtDate   = (dt) => dt ? new Date(dt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";


// ─────────────────────────────────────────────────────────────────────────────
// Mail Success Dialog  (unchanged)
// ─────────────────────────────────────────────────────────────────────────────

const MailSuccessDialog = ({ open, onClose, spoc, studentName }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
    PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}>
    <Box sx={{ background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)", px: 3, pt: 3, pb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleOutlineIcon sx={{ color: "#a5d6a7", fontSize: 22 }} />
          <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: "0.9rem" }}>
            Mail Sent Successfully
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "#90caf9", mt: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Typography sx={{ fontSize: "0.72rem", color: "#bbdefb", mt: 0.5 }}>
        SPOC details sent to <strong style={{ color: "#fff" }}>{studentName}</strong>
      </Typography>
    </Box>
    <DialogContent sx={{ pt: 2, pb: 2.5 }}>
      {spoc ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
          {[
            { icon: <PersonOutlineIcon sx={{ fontSize: 15, color: "#1565c0" }} />, label: "SPOC Name",   value: spoc.spoc_name   || "—" },
            { icon: <EmailOutlinedIcon  sx={{ fontSize: 15, color: "#1565c0" }} />, label: "SPOC Email",  value: spoc.spoc_email  || "—" },
            { icon: <PhoneOutlinedIcon  sx={{ fontSize: 15, color: "#1565c0" }} />, label: "SPOC Mobile", value: spoc.spoc_mobile || "—" },
          ].map(item => (
            <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 1, background: "#f0f7ff", borderRadius: "8px", px: 1.5, py: 1 }}>
              {item.icon}
              <Box>
                <Typography sx={{ fontSize: "0.58rem", color: "#90a4ae", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  {item.label}
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#1e293b" }}>
                  {item.value}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography sx={{ fontSize: "0.78rem", color: "#90a4ae", textAlign: "center", py: 1 }}>
          No SPOC found for this institute.
        </Typography>
      )}
      <Button fullWidth variant="outlined" onClick={onClose} size="small"
        sx={{ mt: 2, borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.75rem", borderColor: "#dde3ea", color: "#546e7a" }}>
        Close
      </Button>
    </DialogContent>
  </Dialog>
);


// ─────────────────────────────────────────────────────────────────────────────
// TAB 1 — Passport Profiles  (your original component, extracted)
// ─────────────────────────────────────────────────────────────────────────────

const PASSPORT_COLUMNS = [
  { id: "name",              label: "Student",    width: 145 },
  { id: "college_name",      label: "College",    width: 145 },
  { id: "email",             label: "Email",      width: 165 },
  { id: "mobile",            label: "Mobile",     width: 95  },
  { id: "is_interested",     label: "Int.",        width: 50  },
  { id: "have_passport",     label: "Pass.",       width: 50  },
  { id: "able_to_pay",       label: "Pay",         width: 50  },
  { id: "affordable_amount", label: "Affordable",  width: 88  },
  { id: "parent_concern",    label: "Parent",      width: 60  },
  { id: "discussed_faculty", label: "Faculty",     width: 60  },
  { id: "mail_action",       label: "Mail",        width: 80  },
];

const PassportTab = ({ bearerToken }) => {
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [page, setPage]             = useState(1);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [ableToPayFilter, setFilter] = useState("All");
  const [search, setSearch]         = useState("");
  const [sendingId, setSendingId]   = useState(null);
  const [mailDialog, setMailDialog] = useState(false);
  const [mailResult, setMailResult] = useState(null);
  const [mailError, setMailError]   = useState("");
  const PAGE_SIZE = 10;

  const fetchData = useCallback(async (pg, filter) => {
    setLoading(true); setError("");
    try {
      const params = { page: pg, page_size: PAGE_SIZE };
      if (filter && filter !== "All") params.able_to_pay = filter;
      const res = await axios.get(`${BASE_URL}/study-tour/survey/passport/profiles`, {
        params,
        headers: bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {},
      });
      setRows(res.data.students || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 0);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load data.");
    } finally { setLoading(false); }
  }, [bearerToken]);

  useEffect(() => { fetchData(page, ableToPayFilter); }, [page, ableToPayFilter, fetchData]);

  const handleFilterChange = (_, val) => { if (!val) return; setFilter(val); setPage(1); };

  const filteredRows = search.trim()
    ? rows.filter(r => {
        const q = search.toLowerCase();
        return r.name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q) || r.college_name?.toLowerCase().includes(q);
      })
    : rows;

  const handleSendMail = async (row) => {
    setSendingId(row.survey_id); setMailError("");
    try {
      const res = await axios.post(
        `${BASE_URL}/study-tour/survey/passport/send-mail/${row.survey_id}`, {},
        { headers: bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {} }
      );
      setRows(prev => prev.map(r => r.survey_id === row.survey_id ? { ...r, is_mail_sent: 1 } : r));
      setMailResult({ spoc: res.data.spoc, studentName: row.name });
      setMailDialog(true);
    } catch (err) {
      setMailError(`${row.survey_id}: ${err?.response?.data?.detail || "Failed to send mail."}`);
    } finally { setSendingId(null); }
  };

  const summaryItems = [
    { icon: <FlightTakeoffIcon sx={{ fontSize: 14 }} />,          label: "Total",          value: total,                                              color: "#1565c0" },
    { icon: <BadgeOutlinedIcon sx={{ fontSize: 14 }} />,          label: "Has Passport",   value: rows.filter(r => r.have_passport === "Yes").length,  color: "#2e7d32" },
    { icon: <PaidOutlinedIcon  sx={{ fontSize: 14 }} />,          label: "Can Pay Full",   value: rows.filter(r => r.able_to_pay === "Yes").length,    color: "#e65100" },
    { icon: <FamilyRestroomOutlinedIcon sx={{ fontSize: 14 }} />, label: "Parent Concern", value: rows.filter(r => r.parent_concern === "Yes").length, color: "#c62828" },
    { icon: <EmailOutlinedIcon sx={{ fontSize: 14 }} />,          label: "Mails Sent",     value: rows.filter(r => r.is_mail_sent === 1).length,       color: "#7b1fa2" },
  ];

  return (
    <>
      {/* Summary Cards */}
      <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
        {summaryItems.map(item => (
          <Paper key={item.label} elevation={0} sx={{
            flex: "1 1 0", p: "7px 10px", borderRadius: "9px",
            border: "1px solid #e8edf2", backgroundColor: "#fff",
            display: "flex", alignItems: "center", gap: 0.8,
          }}>
            <Box sx={{ width: 24, height: 24, borderRadius: "6px", backgroundColor: item.color + "18", display: "flex", alignItems: "center", justifyContent: "center", color: item.color, flexShrink: 0 }}>
              {item.icon}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", lineHeight: 1, color: "#0d1b2a" }}>{item.value}</Typography>
              <Typography sx={{ fontSize: "0.58rem", color: "#90a4ae", lineHeight: 1.3 }}>{item.label}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "#546e7a", textTransform: "uppercase", letterSpacing: "0.05em" }}>Able to Pay:</Typography>
          <ToggleButtonGroup value={ableToPayFilter} exclusive onChange={handleFilterChange} size="small"
            sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, fontSize: "0.62rem", px: 1.2, py: 0.25, lineHeight: 1.6, borderColor: "#dde3ea", color: "#546e7a", "&.Mui-selected": { backgroundColor: "#1565c0", color: "#fff", borderColor: "#1565c0" }, "&.Mui-selected:hover": { backgroundColor: "#0d47a1" } } }}>
            <ToggleButton value="All">All</ToggleButton>
            <ToggleButton value="Yes">Yes</ToggleButton>
            <ToggleButton value="No">No</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <TextField size="small" placeholder="Search name, email, college…"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 13, color: "#90a4ae" }} /></InputAdornment>,
            sx: { borderRadius: "7px", fontSize: "0.65rem", backgroundColor: "#fff", height: 28, "& input": { fontSize: "0.65rem", py: 0 } }
          }}
          sx={{ width: 220, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde3ea" } }}
        />
        {mailError && <Typography sx={{ fontSize: "0.62rem", color: "error.main", ml: 1 }}>{mailError}</Typography>}
        <Typography sx={{ ml: "auto", fontSize: "0.62rem", color: "#90a4ae" }}>
          Showing <strong style={{ color: "#334155" }}>{filteredRows.length}</strong> of <strong style={{ color: "#334155" }}>{total}</strong> records
        </Typography>
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: "10px", border: "1px solid #e8edf2", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 300px)" }}>
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                {PASSPORT_COLUMNS.map(col => (
                  <TableCell key={col.id} sx={{ width: col.width, minWidth: col.width, backgroundColor: "#f1f5f9", fontWeight: 700, fontSize: "0.58rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1.5px solid #e2e8f0", py: 0.8, px: 0.8, whiteSpace: "nowrap" }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={PASSPORT_COLUMNS.length} sx={{ textAlign: "center", py: 5 }}><CircularProgress size={22} sx={{ color: "#1565c0" }} /></TableCell></TableRow>
              ) : error ? (
                <TableRow><TableCell colSpan={PASSPORT_COLUMNS.length} sx={{ textAlign: "center", py: 4 }}><Typography sx={{ fontSize: "0.72rem", color: "error.main" }}>{error}</Typography></TableCell></TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow><TableCell colSpan={PASSPORT_COLUMNS.length} sx={{ textAlign: "center", py: 4 }}><Typography sx={{ fontSize: "0.72rem", color: "#90a4ae" }}>No records found.</Typography></TableCell></TableRow>
              ) : filteredRows.map((row, idx) => {
                const mailSent  = row.is_mail_sent === 1;
                const isSending = sendingId === row.survey_id;
                return (
                  <TableRow key={idx} hover sx={{ "&:hover": { backgroundColor: "#f0f7ff" }, "&:last-child td": { borderBottom: 0 }, backgroundColor: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                    <TableCell sx={{ px: 0.8, py: 0.5, overflow: "hidden" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                        <Avatar sx={{ width: 20, height: 20, fontSize: "0.5rem", fontWeight: 700, backgroundColor: avatarBg(row.name), flexShrink: 0 }}>{initials(row.name)}</Avatar>
                        <Tooltip title={row.name || ""} placement="top">
                          <Typography sx={{ fontWeight: 600, fontSize: "0.65rem", color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.name || "—"}</Typography>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: 0.8, py: 0.5, overflow: "hidden" }}>
                      <Tooltip title={row.college_name || ""} placement="top">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                          <SchoolOutlinedIcon sx={{ fontSize: 10, color: "#b0bec5", flexShrink: 0 }} />
                          <Typography sx={{ fontSize: "0.63rem", color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.college_name || "—"}</Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ px: 0.8, py: 0.5, overflow: "hidden" }}>
                      <Tooltip title={row.email || ""} placement="top">
                        <Typography sx={{ fontSize: "0.6rem", color: "#334155", fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.email || "—"}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ px: 0.8, py: 0.5 }}><Typography sx={{ fontSize: "0.63rem", color: "#334155" }}>{row.mobile || "—"}</Typography></TableCell>
                    <TableCell sx={{ px: 0.8, py: 0.5 }}><YesNoChip value={row.is_interested} /></TableCell>
                    <TableCell sx={{ px: 0.8, py: 0.5 }}><YesNoChip value={row.have_passport} /></TableCell>
                    <TableCell sx={{ px: 0.8, py: 0.5 }}><YesNoChip value={row.able_to_pay} /></TableCell>
                    <TableCell sx={{ px: 0.8, py: 0.5 }}><AmountChip value={row.affordable_amount} /></TableCell>
                    <TableCell sx={{ px: 0.8, py: 0.5 }}><YesNoChip value={row.parent_concern} yesColor="warning" noColor="success" /></TableCell>
                    <TableCell sx={{ px: 0.8, py: 0.5 }}><YesNoChip value={row.discussed_faculty} /></TableCell>
                    <TableCell sx={{ px: 0.8, py: 0.5 }}>
                      {mailSent ? (
                        <Chip icon={<CheckCircleOutlineIcon sx={{ fontSize: "11px !important", color: "#2e7d32 !important" }} />} label="Sent" size="small"
                          sx={{ fontSize: "0.58rem", fontWeight: 700, height: 18, backgroundColor: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7", "& .MuiChip-label": { px: 0.6 } }} />
                      ) : (
                        <Button size="small" variant="contained" disableElevation disabled={isSending} onClick={() => handleSendMail(row)}
                          startIcon={isSending ? <CircularProgress size={9} sx={{ color: "#fff" }} /> : <EmailOutlinedIcon sx={{ fontSize: "11px !important" }} />}
                          sx={{ fontSize: "0.58rem", fontWeight: 700, height: 20, px: 0.8, textTransform: "none", borderRadius: "6px", backgroundColor: "#1565c0", "&:hover": { backgroundColor: "#0d47a1" }, "& .MuiButton-startIcon": { mr: 0.4 }, minWidth: 0 }}>
                          {isSending ? "…" : "Send"}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <>
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", px: 2, py: 0.6 }}>
              <Typography sx={{ mr: 1.5, fontSize: "0.6rem", color: "#90a4ae" }}>Page {page} of {totalPages}</Typography>
              <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} size="small" shape="rounded"
                sx={{ "& .MuiPaginationItem-root": { fontSize: "0.62rem", fontWeight: 600, minWidth: 22, height: 22 }, "& .Mui-selected": { backgroundColor: "#1565c0 !important", color: "#fff" } }} />
            </Box>
          </>
        )}
      </Paper>

      <MailSuccessDialog open={mailDialog} onClose={() => { setMailDialog(false); setMailResult(null); }} spoc={mailResult?.spoc} studentName={mailResult?.studentName} />
    </>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// TAB 2 — Edu Summit Survey  (enriched API)
// ─────────────────────────────────────────────────────────────────────────────

const SUMMIT_COLUMNS = [
  { id: "name",              label: "Name",         width: 145 },
  { id: "institute_name",    label: "Institute",    width: 155 },
  { id: "city_name",         label: "City",         width: 100 },
  { id: "email",             label: "Email",        width: 165 },
  { id: "mobile",            label: "Mobile",       width: 100 },
  { id: "q1_interest",       label: "Interest",     width: 70  },
  { id: "q2_participation",  label: "Participate",  width: 80  },
  { id: "q3_residential",    label: "Residential",  width: 80  },
  { id: "q4_representatives",label: "Reps",         width: 55  },
  { id: "q5_sponsorship",    label: "Sponsor",      width: 70  },
  { id: "submitted_at",      label: "Submitted",    width: 90  },
];

const SummitTab = ({ bearerToken }) => {
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [page, setPage]             = useState(1);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch]         = useState("");
  const [dateFrom, setDateFrom]     = useState("");
  const [dateTo, setDateTo]         = useState("");
  const PAGE_SIZE = 10;

  const fetchData = useCallback(async (pg, from, to) => {
    setLoading(true); setError("");
    try {
      const params = { page: pg, limit: PAGE_SIZE };
      if (from) params.date_from = from;
      if (to)   params.date_to   = to;
      const token = bearerToken || localStorage.getItem("accessToken");
const res = await axios.get(`${BASE_URL}/api/edu-summit-survey/all-enriched`, {
  params,
  headers: token ? { Authorization: `Bearer ${token}` } : {},
});
      setRows(res.data.surveys || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 0);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load survey data.");
    } finally { setLoading(false); }
  }, [bearerToken]);

  useEffect(() => { fetchData(page, dateFrom, dateTo); }, [page, dateFrom, dateTo, fetchData]);

  const handleDateFilter = () => { setPage(1); fetchData(1, dateFrom, dateTo); };
  const handleClearDates = () => { setDateFrom(""); setDateTo(""); setPage(1); };

  const filteredRows = search.trim()
    ? rows.filter(r => {
        const q = search.toLowerCase();
        return r.name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q) || r.institute_name?.toLowerCase().includes(q) || r.city_name?.toLowerCase().includes(q);
      })
    : rows;

  const summaryItems = [
    { icon: <SummarizeOutlinedIcon sx={{ fontSize: 14 }} />,     label: "Total",       value: total,                                                        color: "#1565c0" },
    { icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,    label: "Interested",  value: rows.filter(r => r.q1_interest === "Yes").length,              color: "#2e7d32" },
    { icon: <FlightTakeoffIcon sx={{ fontSize: 14 }} />,         label: "Participate", value: rows.filter(r => r.q2_participation === "Yes").length,         color: "#e65100" },
    { icon: <LocationCityOutlinedIcon sx={{ fontSize: 14 }} />,  label: "Residential", value: rows.filter(r => r.q3_residential === "Yes").length,           color: "#7b1fa2" },
    { icon: <PaidOutlinedIcon sx={{ fontSize: 14 }} />,          label: "Sponsorship", value: rows.filter(r => r.q5_sponsorship === "Yes").length,           color: "#00695c" },
  ];

  return (
    <>
      {/* Summary Cards */}
      <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
        {summaryItems.map(item => (
          <Paper key={item.label} elevation={0} sx={{ flex: "1 1 0", p: "7px 10px", borderRadius: "9px", border: "1px solid #e8edf2", backgroundColor: "#fff", display: "flex", alignItems: "center", gap: 0.8 }}>
            <Box sx={{ width: 24, height: 24, borderRadius: "6px", backgroundColor: item.color + "18", display: "flex", alignItems: "center", justifyContent: "center", color: item.color, flexShrink: 0 }}>
              {item.icon}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", lineHeight: 1, color: "#0d1b2a" }}>{item.value}</Typography>
              <Typography sx={{ fontSize: "0.58rem", color: "#90a4ae", lineHeight: 1.3 }}>{item.label}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Filters Row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1, flexWrap: "wrap" }}>

        {/* Search */}
        <TextField size="small" placeholder="Search name, email, institute, city…"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 13, color: "#90a4ae" }} /></InputAdornment>,
            sx: { borderRadius: "7px", fontSize: "0.65rem", backgroundColor: "#fff", height: 28, "& input": { fontSize: "0.65rem", py: 0 } }
          }}
          sx={{ width: 220, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde3ea" } }}
        />

        {/* Date From */}
        <TextField size="small" type="date" label="From" value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true, sx: { fontSize: "0.62rem" } }}
          InputProps={{ sx: { height: 28, fontSize: "0.65rem", borderRadius: "7px", backgroundColor: "#fff", "& input": { py: 0, fontSize: "0.65rem" } } }}
          sx={{ width: 130, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde3ea" } }}
        />

        {/* Date To */}
        <TextField size="small" type="date" label="To" value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true, sx: { fontSize: "0.62rem" } }}
          InputProps={{ sx: { height: 28, fontSize: "0.65rem", borderRadius: "7px", backgroundColor: "#fff", "& input": { py: 0, fontSize: "0.65rem" } } }}
          sx={{ width: 130, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde3ea" } }}
        />

        {/* Apply / Clear */}
        <Button size="small" variant="contained" disableElevation onClick={handleDateFilter}
          sx={{ height: 28, fontSize: "0.62rem", fontWeight: 700, textTransform: "none", borderRadius: "7px", backgroundColor: "#1565c0", "&:hover": { backgroundColor: "#0d47a1" }, px: 1.5 }}>
          Apply
        </Button>
        {(dateFrom || dateTo) && (
          <Button size="small" variant="outlined" onClick={handleClearDates}
            sx={{ height: 28, fontSize: "0.62rem", fontWeight: 700, textTransform: "none", borderRadius: "7px", borderColor: "#dde3ea", color: "#546e7a", px: 1.2 }}>
            Clear
          </Button>
        )}

        <Typography sx={{ ml: "auto", fontSize: "0.62rem", color: "#90a4ae" }}>
          Showing <strong style={{ color: "#334155" }}>{filteredRows.length}</strong> of <strong style={{ color: "#334155" }}>{total}</strong> records
        </Typography>
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: "10px", border: "1px solid #e8edf2", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 300px)" }}>
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                {SUMMIT_COLUMNS.map(col => (
                  <TableCell key={col.id} sx={{ width: col.width, minWidth: col.width, backgroundColor: "#f1f5f9", fontWeight: 700, fontSize: "0.58rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1.5px solid #e2e8f0", py: 0.8, px: 0.8, whiteSpace: "nowrap" }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={SUMMIT_COLUMNS.length} sx={{ textAlign: "center", py: 5 }}><CircularProgress size={22} sx={{ color: "#1565c0" }} /></TableCell></TableRow>
              ) : error ? (
                <TableRow><TableCell colSpan={SUMMIT_COLUMNS.length} sx={{ textAlign: "center", py: 4 }}><Typography sx={{ fontSize: "0.72rem", color: "error.main" }}>{error}</Typography></TableCell></TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow><TableCell colSpan={SUMMIT_COLUMNS.length} sx={{ textAlign: "center", py: 4 }}><Typography sx={{ fontSize: "0.72rem", color: "#90a4ae" }}>No survey responses found.</Typography></TableCell></TableRow>
              ) : filteredRows.map((row, idx) => (
                <TableRow key={idx} hover sx={{ "&:hover": { backgroundColor: "#f0f7ff" }, "&:last-child td": { borderBottom: 0 }, backgroundColor: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>

                  {/* Name */}
                  <TableCell sx={{ px: 0.8, py: 0.5, overflow: "hidden" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                      <Avatar sx={{ width: 20, height: 20, fontSize: "0.5rem", fontWeight: 700, backgroundColor: avatarBg(row.name), flexShrink: 0 }}>{initials(row.name)}</Avatar>
                      <Tooltip title={row.name || ""} placement="top">
                        <Typography sx={{ fontWeight: 600, fontSize: "0.65rem", color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.name || "—"}</Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>

                  {/* Institute */}
                  <TableCell sx={{ px: 0.8, py: 0.5, overflow: "hidden" }}>
                    <Tooltip title={row.institute_name || ""} placement="top">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                        <SchoolOutlinedIcon sx={{ fontSize: 10, color: "#b0bec5", flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "0.63rem", color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.institute_name || "—"}</Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>

                  {/* City */}
                  <TableCell sx={{ px: 0.8, py: 0.5, overflow: "hidden" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                      <LocationCityOutlinedIcon sx={{ fontSize: 10, color: "#b0bec5", flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.63rem", color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.city_name || "—"}</Typography>
                    </Box>
                  </TableCell>

                  {/* Email */}
                  <TableCell sx={{ px: 0.8, py: 0.5, overflow: "hidden" }}>
                    <Tooltip title={row.email || ""} placement="top">
                      <Typography sx={{ fontSize: "0.6rem", color: "#334155", fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.email || "—"}</Typography>
                    </Tooltip>
                  </TableCell>

                  {/* Mobile */}
                  <TableCell sx={{ px: 0.8, py: 0.5 }}>
                    <Typography sx={{ fontSize: "0.63rem", color: "#334155" }}>{row.mobile || "—"}</Typography>
                  </TableCell>

                  {/* Q1 Interest */}
                  <TableCell sx={{ px: 0.8, py: 0.5 }}><YesNoChip value={row.q1_interest} /></TableCell>

                  {/* Q2 Participation */}
                  <TableCell sx={{ px: 0.8, py: 0.5 }}><YesNoChip value={row.q2_participation} /></TableCell>

                  {/* Q3 Residential */}
                  <TableCell sx={{ px: 0.8, py: 0.5 }}><YesNoChip value={row.q3_residential} /></TableCell>

                  {/* Q4 Representatives */}
                  <TableCell sx={{ px: 0.8, py: 0.5 }}>
                    <Typography sx={{ fontSize: "0.63rem", color: "#334155", textAlign: "center" }}>{row.q4_representatives ?? "—"}</Typography>
                  </TableCell>

                  {/* Q5 Sponsorship */}
                  <TableCell sx={{ px: 0.8, py: 0.5 }}><YesNoChip value={row.q5_sponsorship} /></TableCell>

                  {/* Submitted At */}
                  <TableCell sx={{ px: 0.8, py: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                      <CalendarMonthOutlinedIcon sx={{ fontSize: 10, color: "#b0bec5" }} />
                      <Typography sx={{ fontSize: "0.6rem", color: "#64748b" }}>{fmtDate(row.submitted_at)}</Typography>
                    </Box>
                  </TableCell>

                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <>
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", px: 2, py: 0.6 }}>
              <Typography sx={{ mr: 1.5, fontSize: "0.6rem", color: "#90a4ae" }}>Page {page} of {totalPages}</Typography>
              <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} size="small" shape="rounded"
                sx={{ "& .MuiPaginationItem-root": { fontSize: "0.62rem", fontWeight: 600, minWidth: 22, height: 22 }, "& .Mui-selected": { backgroundColor: "#1565c0 !important", color: "#fff" } }} />
            </Box>
          </>
        )}
      </Paper>
    </>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// ROOT — combines both tabs
// ─────────────────────────────────────────────────────────────────────────────

const PassportProfilesDashboard = ({ bearerToken }) => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box sx={{ p: 1.5, backgroundColor: "#f8fafc", minHeight: "100vh" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: "7px", backgroundColor: "#1565c0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <FlightTakeoffIcon sx={{ color: "#fff", fontSize: 15 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, color: "#0d1b2a", fontSize: "0.88rem", lineHeight: 1.2 }}>
            GIP Thailand — Programme Dashboard
          </Typography>
          <Typography sx={{ fontSize: "0.62rem", color: "#90a4ae" }}>
            Global Education Program · EduSkills Foundation
          </Typography>
        </Box>
      </Box>

      {/* ── Tabs ── */}
      <Paper elevation={0} sx={{ borderRadius: "10px 10px 0 0", border: "1px solid #e8edf2", borderBottom: "none", backgroundColor: "#fff", mb: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{
            minHeight: 36,
            px: 1,
            "& .MuiTabs-indicator": { backgroundColor: "#1565c0", height: 2.5, borderRadius: "2px" },
            "& .MuiTab-root": {
              minHeight: 36, textTransform: "none", fontSize: "0.72rem", fontWeight: 600,
              color: "#90a4ae", px: 2, py: 0,
              "&.Mui-selected": { color: "#1565c0" },
            },
          }}
        >
          <Tab
            label="Passport Profiles"
            icon={<BadgeOutlinedIcon sx={{ fontSize: 14 }} />}
            iconPosition="start"
          />
          <Tab
            label="Edu Summit Survey"
            icon={<SummarizeOutlinedIcon sx={{ fontSize: 14 }} />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* ── Tab Panels ── */}
      <Paper elevation={0} sx={{ borderRadius: "0 0 10px 10px", border: "1px solid #e8edf2", borderTop: "none", backgroundColor: "#f8fafc", p: 1.5 }}>
        {activeTab === 0 && <PassportTab bearerToken={bearerToken} />}
        {activeTab === 1 && <SummitTab  bearerToken={bearerToken} />}
      </Paper>

    </Box>
  );
};

export default PassportProfilesDashboard;